<template>
  <q-dialog
    ref="dialog"
    v-model="shown"
    :persistent="persistent"
    transition-show="fade-in"
    transition-hide="fade-out"
    v-bind="$attrs"
  >
    <q-card
      :style="{ width: maximized ? '100%' : '560px' }"
      class="q-dialog-plugin"
      :class="{ 'q-mx-md': !maximized }"
    >
      <q-card-section class="row items-center q-mx-xs q-pb-none">
        <div
          v-if="title"
          :class="$q.screen.xs ? 'text-h6' : 'text-h5'"
        >
          {{ title }}
        </div>
        <q-chip
          v-if="titleChip"
          class="col-auto q-ml-sm"
          square
          dense
        >
          {{ titleChip }}
        </q-chip>
        <q-space />
        <q-btn
          v-if="!persistent"
          icon="close"
          dense
          flat
          round
          @click="shown = false"
        />
      </q-card-section>
      <q-card-section
        v-if="message"
        class="row q-mx-xs q-my-lg"
      >
        <div
          class="text-body"
        >
          <!-- Preserve whitespace without inserting additonal line breaks -->
          <!-- eslint-disable-next-line vue/singleline-html-element-content-newline -->
          <div style="white-space: pre-wrap;">{{ message }}</div>
        </div>
      </q-card-section>
      <q-card-section
        v-else
        :class="{ 'q-pa-none': noPadding }"
      >
        <slot name="custom-message" />
      </q-card-section>
      <q-card-section
        class="q-gutter-md"
        :class="{
          'reverse': reverseButtonsRow,
          'column': $q.screen.lt.sm && !buttonsInlined,
          'row items-center': $q.screen.gt.xs || buttonsInlined
        }"
      >
        <slot name="buttons" />
        <q-btn
          v-if="cancel"
          :size="$q.screen.lt.sm ? 'md' : 'lg'"
          padding="sm xs"
          class="col"
          :class="{ 'text-capitalize': labelCapitalize }"
          :color="cancelColor"
          :text-color="getTextColor(cancelColor)"
          :disable="cancelDisable"
          @click="cancelClick"
        >
          <q-icon
            v-if="cancelIcon"
            left
            :name="cancelIcon"
            size="xs"
          />
          {{ cancelLabel }}
        </q-btn>
        <q-btn
          v-if="ok"
          :size="$q.screen.lt.sm ? 'md' : 'lg'"
          padding="sm xs"
          class="col"
          :class="{ 'text-capitalize': labelCapitalize }"
          :color="okColor"
          :text-color="getTextColor(okColor)"
          :disable="okDisable"
          @click="okClick"
          @loading="okLoading"
        >
          <q-icon
            v-if="okIcon"
            left
            :name="okIcon"
            size="xs"
          />
          {{ okLabel }}
        </q-btn>
      </q-card-section>
    </q-card>
  </q-dialog>
</template>

<script>
export default {
  name: 'ActionDialog',
  configuration: ['currentSite'],
  props: {
    modelValue: {
      type: Boolean,
      required: false,
      default: false
    },
    title: {
      required: false,
      type: String,
      default: null
    },
    titleChip: {
      required: false,
      type: String,
      default: null
    },
    message: {
      required: false,
      type: String,
      default: null
    },
    ok: {
      required: false,
      type: Boolean,
      default: true
    },
    okLabel: {
      required: false,
      type: String,
      default: 'OK'
    },
    okColor: {
      required: false,
      type: String,
      default: 'primary'
    },
    cancel: {
      required: false,
      type: Boolean,
      default: true
    },
    okDisable: {
      required: false,
      type: Boolean,
      default: false
    },
    okLoading: {
      required: false,
      type: Boolean,
      default: false
    },
    okDismiss: {
      required: false,
      type: Boolean,
      default: true
    },
    cancelLabel: {
      required: false,
      type: String,
      default: 'Cancel'
    },
    cancelColor: {
      required: false,
      type: String,
      default: 'secondary'
    },
    cancelDisable: {
      required: false,
      type: Boolean,
      default: false
    },
    cancelLoading: {
      required: false,
      type: Boolean,
      default: false
    },
    cancelDismiss: {
      required: false,
      type: Boolean,
      default: true
    },
    persistent: {
      type: Boolean,
      required: false,
      default: false
    },
    okIcon: {
      required: false,
      type: String,
      default: null
    },
    cancelIcon: {
      required: false,
      type: String,
      default: null
    },
    buttonsInlined: {
      type: Boolean,
      required: false,
      default: false
    },
    reverseButtonsRow: {
      type: Boolean,
      required: false,
      default: false
    },
    labelCapitalize: {
      type: Boolean,
      required: false,
      default: false
    },
    maximized: {
      type: Boolean,
      required: false,
      default: false
    },
    noPadding: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ['update:model-value', 'hide', 'ok', 'cancel'],
  data () {
    return {
      shown: false
    }
  },
  watch: {
    modelValue () {
      this.shown = this.modelValue
    },
    shown () {
      this.$emit('update:model-value', this.shown)
      if (!this.shown) {
        this.$emit('hide')
      }
    }
  },
  methods: {
    show () {
      this.shown = true
    },
    hide () {
      this.shown = false
    },
    okClick () {
      this.$emit('ok')

      if (this.okDismiss) this.shown = false
    },
    cancelClick () {
      this.$emit('cancel')

      if (this.cancelDismiss) this.shown = false
    },
    getTextColor (color) {
      const brandColor = ['primary', 'secondary', 'positive', 'negative', 'warning', 'info', 'background', 'dark', 'header', 'accent']
      if (!_.includes(brandColor, color)) return 'responsive'
      const colorHex = this.$getSiteColorHex(this.currentSite, color)
      return this.$isColorDark(colorHex) ? 'white' : 'black'
    }
  }
}
</script>
